import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { BellIcon, CheckIcon, ClockIcon, SettingsIcon } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNotifications } from '@/app/notifications-store';
import { useT } from '@/lib/i18n';
import { KIND_META, type AppNotification } from '@/lib/notifications';
import { notify } from '@/lib/notify';
import { cn } from '@/lib/utils';

/**
 * Notifications — the "View all" destination behind the topbar popup (the kit's
 * `pages/notifications`).
 *
 * The kit builds this on TanStack Table with column filters. It is a feed here:
 * `<DataTable>` is the console's one list mechanism (build/10 §5) and Phase 5
 * builds it, so a one-off table now would either duplicate that component or
 * pre-empt its API. Search and the All / Unread split are local state; when the
 * read API and `<DataTable>` both exist, this becomes an instance of it and the
 * rows keep their shape.
 *
 * There is no notifications endpoint, so the feed is empty and says what it is
 * waiting on (see lib/notifications.ts). The actions below mutate an in-memory
 * list, which is what the endpoint will drive when it lands.
 */
export function Notifications() {
  const t = useT();
  const { items, unreadCount, markRead, markAllRead, decide } = useNotifications();
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<'all' | 'unread'>('all');

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (tab === 'unread' && !item.unread) return false;
      if (!q) return true;
      return (
        item.title.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        // The kind is searched by its TRANSLATED label, so typing "Delivery" in
        // an Arabic console finds nothing and typing the Arabic word finds it.
        t(KIND_META[item.kind].label).toLowerCase().includes(q)
      );
    });
  }, [items, query, tab, t]);

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title={t('nav.notifications')}
        description={t('notifications.description')}
        actions={
          <>

            <Button
              size="sm"
              variant="outline"
              disabled={unreadCount === 0}
              onClick={() => {
                markAllRead();
                notify.success(t('notifications.allMarkedRead'));
              }}
            >
              <CheckIcon className="size-4" />
              {t('notifications.markAllRead')}
            </Button>
            <Button size="sm" variant="outline" asChild aria-label={t('notifications.settings')}>
              <Link to="/settings">
                <SettingsIcon className="size-4" />
              </Link>
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          className="max-w-xs grow"
          placeholder={t('notifications.searchPlaceholder')}
          aria-label={t('notifications.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Tabs value={tab} onValueChange={(v) => setTab(v as 'all' | 'unread')}>
          <TabsList>
            <TabsTrigger value="all">{t('notifications.tab.all', { count: items.length })}</TabsTrigger>
            <TabsTrigger value="unread">
              {t('notifications.tab.unread', { count: unreadCount })}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={BellIcon}
          title={t(query || tab === 'unread' ? 'notifications.noMatches' : 'notifications.none')}
          description={t(
            query || tab === 'unread'
              ? 'notifications.noMatches.description'
              : 'notifications.none.description',
          )}
        />
      ) : (
        <ul className="divide-border overflow-hidden rounded-xl border divide-y">
          {visible.map((item) => (
            <NotificationRow
              key={item.id}
              item={item}
              onOpen={() => markRead(item.id)}
              onDecide={(decision) => {
                decide(item.id, decision);
                if (decision === 'accepted') notify.success(t('chrome.accepted.title'), item.title);
                else notify.info(t('chrome.declined.title'), item.title);
              }}
            />
          ))}
        </ul>
      )}

      <p className="text-muted-foreground mt-4 text-xs">{t('notifications.footnote')}</p>
    </div>
  );
}

function NotificationRow({
  item,
  onOpen,
  onDecide,
}: {
  item: AppNotification;
  onOpen: () => void;
  onDecide: (decision: 'accepted' | 'declined') => void;
}) {
  const t = useT();
  const { icon: Icon, label, tone } = KIND_META[item.kind];

  return (
    <li
      className={cn(
        'bg-card flex items-start gap-4 p-4',
        // The unread marker is a start-side border, not left — RTL is
        // first-class (build/10 §1).
        item.unread && 'bg-primary/[0.04] border-primary border-s-2',
      )}
    >
      <div className="flex-none">
        {item.actor ? (
          <Avatar className="size-10">
            <AvatarFallback className="text-xs">{item.actor.initials}</AvatarFallback>
          </Avatar>
        ) : (
          <span
            className={cn(
              'flex size-10 items-center justify-center rounded-full',
              tone === 'danger' ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary',
            )}
          >
            <Icon className="size-4" />
          </span>
        )}
      </div>

      <div className="flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">{item.title}</span>
          <Badge variant="outline" className="text-[10px]">{t(label)}</Badge>
        </div>
        <p className="text-muted-foreground text-sm">{item.description}</p>

        {item.requiresDecision && (
          <div className="flex gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={() => onDecide('accepted')}>
              {t('chrome.accept')}
            </Button>
            <Button size="sm" variant="destructive" onClick={() => onDecide('declined')}>
              {t('chrome.decline')}
            </Button>
          </div>
        )}

        {item.decision && (
          <p className="text-muted-foreground pt-1 text-xs">
            {t(item.decision === 'accepted' ? 'notifications.accepted' : 'notifications.declined')}
          </p>
        )}

        <div className="text-muted-foreground flex items-center gap-1 pt-1 text-xs">
          <ClockIcon className="size-3" />
          {item.time}
        </div>
      </div>

      {item.unread && (
        <Button size="sm" variant="ghost" className="flex-none text-xs" onClick={onOpen}>
          {t('notifications.markRead')}
        </Button>
      )}
    </li>
  );
}
